/**
 * `<SignupRoute>` — `/signup`
 *
 * Per `aidlc-docs/construction/auth-redesign/functional-design/frontend-components.md § 3`.
 *
 * Email + password registration (ENG-35588). Replaces the magic-link
 * "Create your workspace" form. On success the user lands on
 * `/verify-email?email=<addr>` to enter the 6-digit OTC; workspace
 * creation happens later, after they sign in (Flow F).
 *
 * Validation reuses `registerWithPasswordSchema` from
 * `@cometchat-ai/auth/shared` so the client matches the server's
 * contract exactly. WorkOS errors (`AUTH_EMAIL_IN_USE`,
 * `AUTH_WEAK_PASSWORD`, `AUTH_BREACHED_PASSWORD`) surface via
 * `formatApiError` — the WorkOS-verbatim message is shown in the toast
 * description (per FD `business-rules.md § R-ERR`).
 *
 * The visual treatment follows the design-system "Dashboard/Auth/Create
 * Account" fixture: title + description in the card header (owned by
 * <AuthLayout>), email/password (with show/hide) first, then a Google SSO
 * fallback below an "or" divider, mirroring Sign In.
 */

import { authClient, type AuthClientError } from "@cometchat-ai/auth/client";
import {
  registerWithPasswordSchema,
  type RegisterWithPasswordDto,
} from "@cometchat-ai/auth/shared";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Separator,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Eye, EyeOff, Loader2 } from "../components/icons";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { AuthLayout } from "../layouts/auth-layout";
import { formatApiError } from "../lib/format-error";

/**
 * Inline Google "G" mark. Avoids pulling a brand-icon dependency just for
 * one button and keeps the bundle slim. Render small (h-4) — the colors
 * are the official Google brand palette (a fixed brand asset, intentionally
 * exempt from the design-token rule).
 */
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" className={className}>
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}

export function SignupRoute() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const form = useForm<RegisterWithPasswordDto>({
    resolver: zodResolver(registerWithPasswordSchema),
    defaultValues: { email: "", password: "" },
  });

  const mutation = useMutation({
    mutationFn: (values: RegisterWithPasswordDto) => authClient.register(values),
    onSuccess: (result) => {
      navigate(`/verify-email?email=${encodeURIComponent(result.email)}`);
    },
    onError: (err: AuthClientError) => {
      toast.error(t("signup.failed"), { description: formatApiError(err) });
    },
  });

  return (
    <AuthLayout title={t("signup.title")} subtitle={t("signup.subtitle")}>
      <div className="space-y-4">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
            className="space-y-4"
            data-testid="signup-form"
            noValidate
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("signup.email.label")}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      placeholder={t("signup.email.placeholder")}
                      data-testid="signup-email"
                      className="h-12 rounded-xl text-sm placeholder:text-[color:var(--color-fg-subtle)]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("signup.password.label")}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder={t("signup.password.placeholder")}
                        data-testid="signup-password"
                        className="h-12 rounded-xl pr-10 text-sm placeholder:text-[color:var(--color-fg-subtle)]"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={
                          showPassword ? t("signup.hidePassword") : t("signup.showPassword")
                        }
                        aria-pressed={showPassword}
                        className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-muted-foreground"
                      >
                        {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                      </Button>
                    </div>
                  </FormControl>
                  <p className="text-xs text-muted-foreground">{t("signup.password.hint")}</p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="h-10 w-full rounded-xl hover:bg-primary-hover"
              disabled={mutation.isPending}
              data-testid="signup-submit"
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  {t("signup.submitting")}
                </>
              ) : (
                t("signup.submit")
              )}
            </Button>
          </form>
        </Form>

        <div className="flex items-center gap-3">
          <Separator className="flex-1" />
          <span className="text-xs uppercase text-muted-foreground">{t("signup.or")}</span>
          <Separator className="flex-1" />
        </div>

        <Button
          type="button"
          variant="outline"
          className="h-12 w-full rounded-xl"
          onClick={() => authClient.beginGoogleLogin()}
          data-testid="signup-google"
        >
          <GoogleIcon className="mr-2 h-4 w-4" />
          {t("signup.googleButton")}
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          {t("signup.alreadyHaveAccount")}{" "}
          <Link to="/login" className="font-medium text-primary underline-offset-4 hover:underline">
            {t("signup.signIn")}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
